import React, { useEffect, useState } from "react";
import { siteMap } from "internal-sitemap";
import SharedPublicPageHeader from "./shared-public-page-header";
import SharedPublicPageFooter from "./shared-public-page-footer";
import { ReadonlyPageBlock } from "organisms/page-blocks/readonly-page-block/readonly-page-block";
import { useHistory, useParams } from "react-router";
import PageRecord from "models/view-models/page-record";
import useQueryString from "utilities/hooks/routing/use-query-string";
import {
    defaultSharedPageState,
    SharedPageContext,
    SharedPageState,
} from "utilities/contexts/shared-page/shared-page-context";
import SharedPageService from "utilities/services/public/pages/shared-page-service";
import SharedPageBlockService from "utilities/services/public/blocks/shared-page-block-service";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import SharedPageRecipientsService from "utilities/services/public/recipients/shared-page-recipients-service";
import { RouteUtils } from "utilities/route-utils";
import usePageBlocks from "utilities/hooks/domain/notebooks/pages/use-page-blocks";
import Loader from "atoms/loaders/loader";
import NonsubscriberHeader from "molecules/header/unauthorized-header";
interface SharedPublicPageParams {
    pageGuid: string;
}
export interface SharedPublicPageQueryParams {
    recipient?: string;
}

const SharedPublicPage: React.FC = () => {
    const BASE_CSS_CLASSNAME = "c-shared-public-page";

    const { globalState } = useGlobalState();
    const isLoggedIn = globalState.isAuthenticated();
    const { pageGuid } = useParams<SharedPublicPageParams>();
    const { queryParams } = useQueryString<SharedPublicPageQueryParams>();
    const recipientGuid = queryParams.recipient;

    const [sharedPageState, setSharedPageState] = useState<SharedPageState>(
        defaultSharedPageState
    );

    useEffect(() => {
        setSharedPageState({
            pageGuid,
            recipientGuid,
        });
    }, [pageGuid, recipientGuid]);

    const [page, setPage] = useState<PageRecord | undefined>(undefined);
    const {
        populatedBlocks: pageBlocks,
        fetchingBlocks,
        observerRef,
    } = usePageBlocks({
        isPublic: true,
        pageGuid,
        recipientGuid,
    });

    const { get: getPageByGuidApi } = SharedPageService.useGet();
    const { get: getRecipientAccessApi } = SharedPageRecipientsService.useGet();

    const { list: getBlocksByPageGuidApi } =
        SharedPageBlockService.useGetByPage();

    const history = useHistory();

    useEffect(() => {
        const checkAuthenticatedRedirect = async () => {
            try {
                if (!recipientGuid) {
                    return;
                }
                const sharedPageRecipientResponse = await getRecipientAccessApi(
                    {
                        pageGuid,
                    },
                    { recipientGuid: recipientGuid }
                );
                const sharedPageAccess =
                    sharedPageRecipientResponse.resultObject;
                if (
                    sharedPageAccess != null &&
                    sharedPageAccess.pageId != null
                ) {
                    if (
                        sharedPageAccess.expiresOn
                            ? new Date(sharedPageAccess.expiresOn) > new Date()
                            : false
                    ) {
                        return;
                    }

                    const redirectUrl = RouteUtils.getUrl(
                        siteMap.notebooks.notebook.page,
                        {
                            notebookId: 0,
                            pageId: sharedPageAccess.pageId.toString(),
                        }
                    );

                    history.push(redirectUrl);
                }
            } catch (error) {
                //No action, allow the public page access.
            }
        };
        if (
            isLoggedIn &&
            globalState.currentIdentity?.hasActiveSubscription()
        ) {
            checkAuthenticatedRedirect();
        }
    }, [
        getRecipientAccessApi,
        globalState.currentIdentity,
        globalState.currentIdentity?.hasActiveSubscription,
        pageGuid,
        history,
        isLoggedIn,
        recipientGuid,
    ]);

    useEffect(() => {
        const loadPage = async () => {
            try {
                const getPageResponse = await getPageByGuidApi(
                    {
                        pageGuid,
                    },
                    {
                        recipient: recipientGuid,
                    }
                );
                const page = getPageResponse.resultObject;
                setPage(page);
            } catch (error) {
                history.push(siteMap.errors.notFound);
            }
        };
        loadPage();
    }, [
        getBlocksByPageGuidApi,
        getPageByGuidApi,
        pageGuid,
        history,
        recipientGuid,
    ]);

    return (
        <SharedPageContext.Provider value={sharedPageState}>
            <div className={`${BASE_CSS_CLASSNAME}`}>
                <div className={`${BASE_CSS_CLASSNAME}__banner`}>
                    {/* Per designs, using non-subscriber-style header for all users, regardless of subscription status */}
                    <NonsubscriberHeader />
                </div>
                <main className={`${BASE_CSS_CLASSNAME}__main`}>
                    <SharedPublicPageHeader
                        page={page}
                        recipientGuid={recipientGuid}
                        pageGuid={pageGuid}
                    />
                    <div className={`${BASE_CSS_CLASSNAME}__wrapper`}>
                        <div className={`${BASE_CSS_CLASSNAME}__contentArea`}>
                            {pageBlocks.map((block) => (
                                <ReadonlyPageBlock
                                    key={block.id}
                                    block={block}
                                />
                            ))}
                            <div
                                ref={(node) => {
                                    observerRef(node);
                                }}
                                className={`${BASE_CSS_CLASSNAME}__sentinel`}
                            />
                            {fetchingBlocks && (
                                <div
                                    className={`${BASE_CSS_CLASSNAME}__bottomLoader`}>
                                    <Loader />
                                </div>
                            )}
                        </div>
                    </div>
                </main>
                {!isLoggedIn && <SharedPublicPageFooter />}
            </div>
        </SharedPageContext.Provider>
    );
};

export default SharedPublicPage;
